<template>
	<div class="wrapper">
		<div class="murk">
			<BalanceInfo :title="setTitle" show-switch-account :show-balance="setBalanceText" />

			<div class="failed-purchase">
				<div class="failed-purchase__header maintenance">
					<img :src="setIcon" alt="Purchase Failed" width="110" height="100" />
					<div class="failed-purchase__header__title">
						<p>{{ $t('checkout.purchase-failed') }}</p>
						<p v-if="!!setFailReason" class="failed-purchase__header__title__reason">{{ setFailReason }}</p>
					</div>
					<div class="failed-purchase__header__message">
						{{ setFailMessage }}
						<br />
						<span v-if="showErrorCode">{{ $t('checkout.error-code', { errorCode }) }}</span>
					</div>
				</div>

				<div v-if="orderNumber" class="failed-purchase__details">
					<div class="order-details">
						<div class="order-details__row">
							<span>
								{{ $t('checkout.order-number') }}
							</span>
							<span>{{ orderNumber }}</span>
						</div>
					</div>
				</div>

				<div class="failed-purchase__contacts">
					<i18n path="checkout.questions-comments" tag="p">
						<template v-slot:supportLink>
							<a
								v-t="'checkout.support-link-text'"
								:href="supportLink"
								target="_blank"
								@click="onSupportClick"
							></a>
						</template>
					</i18n>
				</div>

				<div class="failed-purchase__action">
					<FormButton
						v-if="setButtonDisplay"
						class="btn-primary"
						:label="setButtonText"
						:button-click="buttonAction"
					></FormButton>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import BalanceInfo from '@/components/common/balance-info';
import { FormButton } from '@arena/toolkit';
import config from '@/config/environment-config';
import links from '@/config/links';

export default {
	name: 'PurchaseFailed',
	components: {
		BalanceInfo,
		FormButton,
	},
	props: {
		orderNumber: {
			type: String,
			default: null,
		},
		returnURL: {
			type: String,
			default: null,
		},
		errorCode: {
			type: [String, Number],
			default: null,
		},
	},
	data() {
		return {};
	},
	computed: {
		appType() {
			return this.$store.state.appType;
		},
		setIcon() {
			return this.$store.state.region === 'th'
				? require('@/images/purchase-maintenance.png')
				: require('@/images/purchase-failed.png');
		},
		setFailReason() {
			const messageKey = 'checkout.purchase-failed-reason';
			if (this.$t(messageKey) !== messageKey) {
				return this.$t(messageKey);
			} else {
				return '';
			}
		},
		setFailMessage() {
			if (this.$route.query.error_code) {
				const code = decodeURI(this.$route.query.error_code),
					messageKey = `error.form.${code}`;
				if (this.$t(messageKey) !== messageKey) {
					return `${this.$t(messageKey)} (${code})`;
				}
			} else if (this.$store.state.region === 'th') {
				return this.$t('checkout.end-of-service');
			}

			return `${this.$t('checkout.unable-to-complete-purchase')} ${
				this.$route.query.error_code ? `(${this.$route.query.error_code})` : ''
			}`;
		},
		showErrorCode() {
			if (this.$store.state.region === 'th') {
				return false;
			} else if (!this.errorCode) {
				return false;
			}

			return true;
		},
		setButtonDisplay() {
			return !this.$route.query.error_code && this.$store.state.region !== 'th';
		},
		setButtonText() {
			if (this.appType !== 'game') {
				return this.$t('checkout.try-again');
			}
			return this.$t('checkout.close-window');
		},
		supportLink() {
			return links[this.region].supportLink;
		},
		setTitle() {
			if (this.$route.query.viewMode === 'compact') {
				return '';
			} else if (this.$store.state.region === 'global') {
				return this.$t('checkout.buy-nx');
			} else {
				return this.$t('add-balance');
			}
		},
		setBalanceText() {
			return !this.$route.query.viewMode === 'compact';
		},
	},
	mounted() {
		this.$a2s.view('error_imp', {
			name: 'error_code',
			value: this.errorCode,
			objectTag: 'purchase_error',
		});

		if (!this.orderNumber) {
			if (this.$route.query.error_code) {
				dataLayer.push({
					event: 'Add Balance Error',
					'Add Balance Error Message': this.$te(`error.form.${this.$route.query.error_code}`)
						? this.$t(`error.form.${this.$route.query.error_code}`, 'en')
						: this.$route.query.error_code,
				});
			} else if (this.errorCode) {
				dataLayer.push({
					event: 'Add Balance Error',
					'Add Balance Error Message': this.$te(`error.form.${this.errorCode}`)
						? this.$t(`error.form.${this.errorCode}`, 'en')
						: this.errorCode,
				});
			}
		}
	},
	methods: {
		buttonAction() {
			this.$a2s.click('error_close');
			if (this.appType === 'game') {
				window.close();
			} else if (this.returnURL) {
				this.goto(this.returnURL);
			} else {
				window.location.href = `${config[process.env.VUE_APP_ENV][environment.region].wallet}/${
					this.$i18n.locale
				}/add-balance`;
			}
		},
		onSupportClick() {
			this.$a2s.click('error_support');
		},
	},
};
</script>

<style lang="less" scoped>
.failed-purchase {
	display: flex;
	flex-wrap: wrap;
	max-width: 640px;
	margin-top: 50px;
	margin-left: auto;
	margin-right: auto;
	&__header {
		flex: 0 0 100%;
		max-width: 100%;
		color: #fff;
		text-align: center;
		line-height: 1;
		padding-bottom: 36px;
		border-bottom: 2px solid rgba(#fff, 50%);

		& > img {
			margin-bottom: 40px;
		}

		&__title {
			font-size: 24px;

			&__reason {
				font-size: 14px;
				line-height: 18px;
				color: rgba(255, 255, 255, 0.8);
			}
		}
		&__message {
			padding-top: 8px;
			line-height: 1.2;
			color: #ff1744;
			white-space: pre-wrap;
		}
	}
	&__details {
		flex: 0 0 100%;
		max-width: 100%;
		border-bottom: 1px solid rgba(#fff, 20%);
		.order-details {
			padding-top: 2px;
			padding-bottom: 18px;
			&__row {
				padding-top: 15px;
				> span {
					display: block;
					&:last-child {
						padding-top: 8px;
						color: #fff;
					}
				}
			}
		}
	}
	&__contacts {
		flex: 0 0 100%;
		max-width: 100%;
		text-align: center;
		padding-top: 32px;
		a {
			text-decoration: underline;
			text-underline-position: under;
			text-transform: capitalize;
			color: inherit;
			&:hover {
				color: #fff;
			}
		}
	}
	&__action {
		display: flex;
		flex-wrap: wrap;
		flex: 0 0 100%;
		max-width: 100%;
		justify-content: center;
		button {
			min-width: 320px;
			width: auto;
			margin-top: 26px;
		}
	}
}

@media (max-width: 599px) {
	.failed-purchase {
		&__header {
			padding-bottom: 17px;
			&__message {
				padding-top: 11px;
			}
		}
		&__action {
			button {
				min-width: 100%;
				width: 100%;
			}
		}
	}
}

@media (min-width: 1024px) {
	.failed-purchase {
		&__details {
			.order-details {
				padding-bottom: 18px;
				display: table;
				&__row {
					display: table-row;
					> span {
						display: table-cell;
						padding-top: 17px;
						&:last-child {
							padding-left: 29px;
							color: #fff;
						}
					}
				}
			}
		}
	}
}
</style>
